import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useLocation } from 'react-router-dom'
import { useModal } from './ModalProvider'
import { Button } from '../components/ui/button'

type Props = {
  warnAfterMs?: number
  logoutAfterMs?: number
  disabledPaths?: Array<string | RegExp>
}

export default function SessionInactivityManager({
  warnAfterMs = 10 * 60 * 1000,
  logoutAfterMs = 11 * 60 * 1000,
  disabledPaths = ['/login*', '/signup*', '/leaderboard*']
}: Props) {
  const { signOut } = useAuth()
  const { showModal, hideModal, setModalContent } = useModal()
  const location = useLocation()

  const lastActive = useRef<number>(Date.now())
  const [countdown, setCountdown] = useState<number>(0)
  const warnShown = useRef<boolean>(false)
  const intervalId = useRef<ReturnType<typeof setInterval> | null>(null)

  const pathname = location.pathname

  const isDisabledRoute = useMemo(() => {
    const match = (pat: string | RegExp) => {
      if (pat instanceof RegExp) return pat.test(pathname)
      if (pat.endsWith('*')) return pathname.startsWith(pat.slice(0, -1))
      return pathname === pat
    }
    return disabledPaths.some(match)
  }, [pathname, disabledPaths])

  useEffect(() => {
    if (isDisabledRoute) {
      if (intervalId.current) {
        clearInterval(intervalId.current)
        intervalId.current = null
      }
      if (warnShown.current) {
        hideModal()
        warnShown.current = false
      }
      lastActive.current = Date.now()
    } else {
      lastActive.current = Date.now()
    }
  }, [isDisabledRoute, hideModal])

  useEffect(() => {
    if (isDisabledRoute) return

    const bump = () => {
      lastActive.current = Date.now()
      if (warnShown.current) {
        hideModal()
        warnShown.current = false
      }
    }

    const events = [
      'mousemove',
      'keydown',
      'click',
      'scroll',
      'touchstart',
      'visibilitychange'
    ]
    events.forEach(e => window.addEventListener(e, bump, { passive: true }))

    intervalId.current = setInterval(() => {
      const idle = Date.now() - lastActive.current

      if (!warnShown.current && idle >= warnAfterMs && idle < logoutAfterMs) {
        warnShown.current = true
        const remainMs = logoutAfterMs - idle
        const secs = Math.ceil(remainMs / 1000)
        setCountdown(secs)
        showModal(
          <WarnContent
            seconds={secs}
            onStay={bump}
          />,
          {
            title: 'Session expiring soon',
            size: 'md',
            closeOnBackdrop: false
          }
        )
      }

      if (warnShown.current && idle < logoutAfterMs) {
        const remainMs = logoutAfterMs - idle
        const secs = Math.max(0, Math.ceil(remainMs / 1000))
        setCountdown(secs)
        setModalContent(
          <WarnContent
            seconds={secs}
            onStay={bump}
          />
        )
      }

      if (idle >= logoutAfterMs) {
        hideModal()
        signOut()
        if (intervalId.current) {
          clearInterval(intervalId.current)
          intervalId.current = null
        }
      }
    }, 1000)

    return () => {
      events.forEach(e => window.removeEventListener(e, bump))
      if (intervalId.current) {
        clearInterval(intervalId.current)
        intervalId.current = null
      }
    }
  }, [
    isDisabledRoute,
    hideModal,
    setModalContent,
    showModal,
    signOut,
    warnAfterMs,
    logoutAfterMs
  ])

  return null
}

function WarnContent({
  seconds,
  onStay
}: {
  seconds: number
  onStay: () => void
}) {
  return (
    <div className='space-y-3'>
      <p className='text-sm text-gray-700'>
        You’ve been inactive. You’ll be signed out for security in{' '}
        <span className='font-semibold'>{seconds}s</span>.
      </p>
      <div className='flex justify-end gap-2 pt-3'>
        <Button onClick={onStay}>I’m still here</Button>
      </div>
    </div>
  )
}
